import { useState, useMemo, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Calendar as CalendarIcon,
  CalendarDays,
  CalendarRange,
  Clock,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, subWeeks, subMonths, getWeek, getYear, parse, isValid } from "date-fns";

/**
 * DateViewSelector - Smart date filtering component for Shipment Costing
 * Supports Daily, Weekly, Monthly, and Custom Range views
 */
const DateViewSelector = ({
  onDateRangeChange,
  orders = [],
  compact = false,
  skipInitialFire = false,
  // Optional client-code filter — when provided, renders an inline input in
  // the compact strip so operators can filter by client without leaving the
  // date filter row.
  clientFilter,
  onClientFilterChange,
}) => {
  // When the page arrives via a deep-link (e.g. ?tracking=) the caller
  // wants a global search with no period filter. Start the selector in
  // "custom" with no dates so the Monthly/Weekly/Daily blue chip is OFF
  // and operators visually understand no date filter is active.
  const [viewMode, setViewMode] = useState(skipInitialFire ? "custom" : "monthly");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth()); // 0-11
  const [selectedWeek, setSelectedWeek] = useState(getWeek(new Date()));
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [customDateFrom, setCustomDateFrom] = useState(null);
  const [customDateTo, setCustomDateTo] = useState(null);
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);
  const initialFired = useRef(false);

  // Fire the default monthly filter on initial mount — unless the parent
  // explicitly opted out (e.g. tracking deep-link wants a global search).
  useEffect(() => {
    if (!initialFired.current) {
      initialFired.current = true;
      if (skipInitialFire) return;
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = endOfMonth(monthStart);
      onDateRangeChange?.(formatToDDMMYY(monthStart), formatToDDMMYY(monthEnd));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate year options from 2018 to current year
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= 2018; y--) {
      years.push(y);
    }
    return years;
  }, []);

  // Month names
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Week options for selected year (1-52)
  const weekOptions = useMemo(() => {
    const weeks = [];
    for (let w = 1; w <= 52; w++) {
      weeks.push(w);
    }
    return weeks;
  }, []);

  // Convert DDMMYY to Date object
  const parseDDMMYY = (dateStr) => {
    if (!dateStr || dateStr.length !== 6) return null;
    const day = parseInt(dateStr.substring(0, 2));
    const month = parseInt(dateStr.substring(2, 4)) - 1;
    const year = parseInt(dateStr.substring(4, 6));
    const fullYear = year >= 0 && year <= 30 ? 2000 + year : 1900 + year;
    return new Date(fullYear, month, day);
  };

  // Convert Date to DDMMYY string
  const formatToDDMMYY = (date) => {
    if (!date || !isValid(date)) return "";
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}${month}${year}`;
  };

  // Calculate order counts by month for the selected year
  const orderCountsByMonth = useMemo(() => {
    const counts = Array(12).fill(0);
    orders.forEach(order => {
      const date = parseDDMMYY(order.order_date);
      if (date && date.getFullYear() === selectedYear) {
        counts[date.getMonth()]++;
      }
    });
    return counts;
  }, [orders, selectedYear]);

  // Calculate order counts by week for the selected year
  const orderCountsByWeek = useMemo(() => {
    const counts = {};
    orders.forEach(order => {
      const date = parseDDMMYY(order.order_date);
      if (date && date.getFullYear() === selectedYear) {
        const week = getWeek(date);
        counts[week] = (counts[week] || 0) + 1;
      }
    });
    return counts;
  }, [orders, selectedYear]);

  // Apply date filter based on view mode
  const applyDateFilter = (mode, year, month, week, date, fromDate, toDate) => {
    let from = "";
    let to = "";

    switch (mode) {
      case "daily":
        if (date) {
          from = formatToDDMMYY(date);
          to = formatToDDMMYY(date);
        }
        break;
      case "weekly":
        if (year && week) {
          // Use date-fns to properly calculate week start/end
          // Find the first Monday of the year, then add (week-1) * 7 days
          const jan4 = new Date(year, 0, 4); // Jan 4 is always in week 1 (ISO standard)
          const firstWeekStart = startOfWeek(jan4, { weekStartsOn: 1 });
          const targetWeekStart = new Date(firstWeekStart);
          targetWeekStart.setDate(firstWeekStart.getDate() + (week - 1) * 7);
          const targetWeekEnd = endOfWeek(targetWeekStart, { weekStartsOn: 1 });
          from = formatToDDMMYY(targetWeekStart);
          to = formatToDDMMYY(targetWeekEnd);
        }
        break;
      case "monthly":
        if (year !== undefined && month !== undefined) {
          const monthStart = new Date(year, month, 1);
          const monthEnd = endOfMonth(monthStart);
          from = formatToDDMMYY(monthStart);
          to = formatToDDMMYY(monthEnd);
        }
        break;
      case "custom":
        if (fromDate) from = formatToDDMMYY(fromDate);
        if (toDate) to = formatToDDMMYY(toDate);
        break;
      default:
        break;
    }

    onDateRangeChange?.(from, to);
  };

  // Handle view mode change
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (mode === "monthly") {
      applyDateFilter(mode, selectedYear, selectedMonth, null, null, null, null);
    } else if (mode === "weekly") {
      applyDateFilter(mode, selectedYear, null, selectedWeek, null, null, null);
    } else if (mode === "daily") {
      applyDateFilter(mode, null, null, null, selectedDate, null, null);
    } else if (mode === "custom") {
      applyDateFilter(mode, null, null, null, null, customDateFrom, customDateTo);
    }
  };

  // Handle year change
  const handleYearChange = (year) => {
    const yearNum = parseInt(year);
    setSelectedYear(yearNum);
    if (viewMode === "monthly") {
      applyDateFilter("monthly", yearNum, selectedMonth, null, null, null, null);
    } else if (viewMode === "weekly") {
      applyDateFilter("weekly", yearNum, null, selectedWeek, null, null, null);
    }
  };

  // Handle month change
  const handleMonthChange = (month) => {
    const monthNum = parseInt(month);
    setSelectedMonth(monthNum);
    applyDateFilter("monthly", selectedYear, monthNum, null, null, null, null);
  };

  // Handle week change
  const handleWeekChange = (week) => {
    const weekNum = parseInt(week);
    setSelectedWeek(weekNum);
    applyDateFilter("weekly", selectedYear, null, weekNum, null, null, null);
  };

  // Handle daily date change
  const handleDailyDateChange = (date) => {
    setSelectedDate(date);
    applyDateFilter("daily", null, null, null, date, null, null);
  };

  // Quick filter handlers
  const handleQuickFilter = (type) => {
    const today = new Date();
    let from, to;

    switch (type) {
      case "today":
        from = to = today;
        setViewMode("daily");
        setSelectedDate(today);
        break;
      case "yesterday":
        from = to = subDays(today, 1);
        setViewMode("daily");
        setSelectedDate(from);
        break;
      case "thisWeek":
        from = startOfWeek(today, { weekStartsOn: 1 });
        to = endOfWeek(today, { weekStartsOn: 1 });
        setViewMode("weekly");
        setSelectedYear(getYear(today));
        setSelectedWeek(getWeek(today));
        break;
      case "lastWeek":
        const lastWeekDate = subWeeks(today, 1);
        from = startOfWeek(lastWeekDate, { weekStartsOn: 1 });
        to = endOfWeek(lastWeekDate, { weekStartsOn: 1 });
        setViewMode("weekly");
        setSelectedYear(getYear(lastWeekDate));
        setSelectedWeek(getWeek(lastWeekDate));
        break;
      case "thisMonth":
        from = startOfMonth(today);
        to = endOfMonth(today);
        setViewMode("monthly");
        setSelectedYear(today.getFullYear());
        setSelectedMonth(today.getMonth());
        break;
      case "lastMonth":
        const lastMonthDate = subMonths(today, 1);
        from = startOfMonth(lastMonthDate);
        to = endOfMonth(lastMonthDate);
        setViewMode("monthly");
        setSelectedYear(lastMonthDate.getFullYear());
        setSelectedMonth(lastMonthDate.getMonth());
        break;
      case "thisYear":
        from = new Date(today.getFullYear(), 0, 1);
        to = new Date(today.getFullYear(), 11, 31);
        setViewMode("custom");
        setCustomDateFrom(from);
        setCustomDateTo(to);
        break;
      case "clear":
        setViewMode("monthly");
        setSelectedYear(new Date().getFullYear());
        setSelectedMonth(new Date().getMonth());
        onDateRangeChange?.("", "");
        return;
      default:
        return;
    }

    onDateRangeChange?.(formatToDDMMYY(from), formatToDDMMYY(to));
  };

  // Navigate month/week
  const handleNavigate = (direction) => {
    if (viewMode === "monthly") {
      let newMonth = selectedMonth + direction;
      let newYear = selectedYear;
      if (newMonth > 11) {
        newMonth = 0;
        newYear++;
      } else if (newMonth < 0) {
        newMonth = 11;
        newYear--;
      }
      setSelectedMonth(newMonth);
      setSelectedYear(newYear);
      applyDateFilter("monthly", newYear, newMonth, null, null, null, null);
    } else if (viewMode === "weekly") {
      let newWeek = selectedWeek + direction;
      let newYear = selectedYear;
      if (newWeek > 52) {
        newWeek = 1;
        newYear++;
      } else if (newWeek < 1) {
        newWeek = 52;
        newYear--;
      }
      setSelectedWeek(newWeek);
      setSelectedYear(newYear);
      applyDateFilter("weekly", newYear, null, newWeek, null, null, null);
    } else if (viewMode === "daily") {
      const newDate = new Date(selectedDate);
      newDate.setDate(newDate.getDate() + direction);
      setSelectedDate(newDate);
      applyDateFilter("daily", null, null, null, newDate, null, null);
    }
  };

  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {/* Cleared / Global Search hint when no date filter is active */}
        {viewMode === "custom" && !customDateFrom && !customDateTo && (
          <div
            className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md bg-amber-50 text-amber-800 border border-amber-200"
            data-testid="global-search-badge"
            title="No date filter is active — searching across all months."
          >
            🌐 Global Search · No date filter
          </div>
        )}
        {/* Compact View Mode */}
        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
          {["daily", "weekly", "monthly", "custom"].map((mode) => (
            <button
              key={mode}
              onClick={() => handleViewModeChange(mode)}
              data-testid={`date-view-mode-${mode}`}
              className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                viewMode === mode
                  ? "bg-blue-600 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {mode === "custom" ? "Range" : mode}
            </button>
          ))}
        </div>

        {/* Year Select — hidden in Range/custom mode */}
        {viewMode !== "custom" && (
          <Select value={String(selectedYear)} onValueChange={handleYearChange}>
            <SelectTrigger className="w-24 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Compact From / To range pickers (custom mode) */}
        {viewMode === "custom" && (
          <>
            <span className="text-[11px] text-slate-500">From</span>
            <Popover open={showFromCalendar} onOpenChange={setShowFromCalendar}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs px-2 gap-1"
                  data-testid="date-view-custom-from-btn"
                >
                  <CalendarIcon className="w-3 h-3" />
                  {customDateFrom ? format(customDateFrom, "dd MMM yyyy") : "Pick date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={customDateFrom}
                  onSelect={(date) => {
                    setCustomDateFrom(date);
                    setShowFromCalendar(false);
                    if (date && customDateTo) {
                      applyDateFilter("custom", null, null, null, null, date, customDateTo);
                    }
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <span className="text-[11px] text-slate-500">To</span>
            <Popover open={showToCalendar} onOpenChange={setShowToCalendar}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs px-2 gap-1"
                  data-testid="date-view-custom-to-btn"
                >
                  <CalendarIcon className="w-3 h-3" />
                  {customDateTo ? format(customDateTo, "dd MMM yyyy") : "Pick date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={customDateTo}
                  onSelect={(date) => {
                    setCustomDateTo(date);
                    setShowToCalendar(false);
                    if (customDateFrom && date) {
                      applyDateFilter("custom", null, null, null, null, customDateFrom, date);
                    }
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            {(!customDateFrom || !customDateTo) && (
              <span className="text-[11px] text-amber-700">
                pick both dates to apply
              </span>
            )}
          </>
        )}

        {/* Month/Week Select based on view mode */}
        {viewMode === "monthly" && (
          <Select value={String(selectedMonth)} onValueChange={handleMonthChange}>
            <SelectTrigger className="w-28 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthNames.map((m, i) => (
                <SelectItem key={i} value={String(i)}>
                  {m} {orderCountsByMonth[i] > 0 && `(${orderCountsByMonth[i]})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {viewMode === "weekly" && (
          <Select value={String(selectedWeek)} onValueChange={handleWeekChange}>
            <SelectTrigger className="w-28 h-8 text-xs">
              <SelectValue placeholder="Week" />
            </SelectTrigger>
            <SelectContent>
              {weekOptions.map((w) => (
                <SelectItem key={w} value={String(w)}>
                  Week {w} {orderCountsByWeek[w] > 0 && `(${orderCountsByWeek[w]})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {viewMode === "daily" && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs">
                <CalendarIcon className="w-3 h-3 mr-1" />
                {format(selectedDate, "dd MMM yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && handleDailyDateChange(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        )}

        {/* Navigation */}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleNavigate(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleNavigate(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Quick Filters */}
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" className="h-8 text-xs px-2" onClick={() => handleQuickFilter("today")}>
            Today
          </Button>
          <Button variant="ghost" size="sm" className="h-8 text-xs px-2" onClick={() => handleQuickFilter("thisMonth")}>
            This Month
          </Button>
          <Button variant="ghost" size="sm" className="h-8 text-xs px-2 text-red-600" onClick={() => handleQuickFilter("clear")}>
            Clear
          </Button>
        </div>

        {/* Client code filter — only rendered when the parent wires the
            handler. Uses a datalist for autocomplete against the client
            codes already visible in the current orders slice. */}
        {typeof onClientFilterChange === "function" && (
          <div className="flex items-center gap-1 ml-1 pl-2 border-l border-slate-200">
            <span className="text-[11px] text-slate-500">Client</span>
            <input
              type="text"
              list="date-view-client-codes"
              value={clientFilter || ""}
              onChange={(e) => onClientFilterChange(e.target.value.toUpperCase())}
              placeholder="Code"
              data-testid="date-view-client-filter"
              className="h-8 w-28 rounded-md border border-slate-200 bg-white px-2 text-xs uppercase tracking-wide placeholder:normal-case placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <datalist id="date-view-client-codes">
              {[...new Set((orders || []).map((o) => (o?.client_code || "").toUpperCase()).filter(Boolean))]
                .sort()
                .slice(0, 200)
                .map((c) => (
                  <option key={c} value={c} />
                ))}
            </datalist>
            {clientFilter && (
              <button
                type="button"
                onClick={() => onClientFilterChange("")}
                data-testid="date-view-client-filter-clear"
                className="text-slate-400 hover:text-red-600 text-[13px] leading-none px-1"
                title="Clear client filter"
              >
                ×
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <Card className="border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
      <CardContent className="p-4">
        <div className="space-y-4">
          {/* View Mode Selector */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-slate-700">View:</span>
            <div className="flex rounded-lg border border-slate-200 overflow-hidden shadow-sm">
              {[
                { mode: "daily", icon: CalendarIcon, label: "Daily" },
                { mode: "weekly", icon: CalendarDays, label: "Weekly" },
                { mode: "monthly", icon: CalendarRange, label: "Monthly" },
                { mode: "custom", icon: Clock, label: "Custom" },
              ].map(({ mode, icon: Icon, label }) => (
                <button
                  key={mode}
                  onClick={() => handleViewModeChange(mode)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-all ${
                    viewMode === mode
                      ? "bg-blue-600 text-white shadow-inner"
                      : "bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Date Selectors based on View Mode */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Year Selector (for monthly and weekly) */}
            {(viewMode === "monthly" || viewMode === "weekly") && (
              <>
                <span className="text-sm text-slate-600">Year:</span>
                <Select value={String(selectedYear)} onValueChange={handleYearChange}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}

            {/* Month Selector */}
            {viewMode === "monthly" && (
              <>
                <span className="text-sm text-slate-600">Month:</span>
                <Select value={String(selectedMonth)} onValueChange={handleMonthChange}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {monthNames.map((m, i) => (
                      <SelectItem key={i} value={String(i)}>
                        <div className="flex items-center justify-between w-full">
                          <span>{m}</span>
                          {orderCountsByMonth[i] > 0 && (
                            <Badge variant="secondary" className="ml-2 text-xs">
                              {orderCountsByMonth[i]}
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Month Navigation */}
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" onClick={() => handleNavigate(-1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleNavigate(1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </>
            )}

            {/* Week Selector */}
            {viewMode === "weekly" && (
              <>
                <span className="text-sm text-slate-600">Week:</span>
                <Select value={String(selectedWeek)} onValueChange={handleWeekChange}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Select week" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {weekOptions.map((w) => (
                      <SelectItem key={w} value={String(w)}>
                        <div className="flex items-center justify-between w-full">
                          <span>Week {w}</span>
                          {orderCountsByWeek[w] > 0 && (
                            <Badge variant="secondary" className="ml-2 text-xs">
                              {orderCountsByWeek[w]}
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Week Navigation */}
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" onClick={() => handleNavigate(-1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleNavigate(1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </>
            )}

            {/* Daily Date Picker */}
            {viewMode === "daily" && (
              <>
                <span className="text-sm text-slate-600">Date:</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-44 justify-start">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {format(selectedDate, "dd MMMM yyyy")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => date && handleDailyDateChange(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                {/* Day Navigation */}
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" onClick={() => handleNavigate(-1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleNavigate(1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </>
            )}

            {/* Custom Range Pickers */}
            {viewMode === "custom" && (
              <>
                <span className="text-sm text-slate-600">From:</span>
                <Popover open={showFromCalendar} onOpenChange={setShowFromCalendar}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-40 justify-start">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {customDateFrom ? format(customDateFrom, "dd MMM yyyy") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={customDateFrom}
                      onSelect={(date) => {
                        setCustomDateFrom(date);
                        setShowFromCalendar(false);
                        if (date && customDateTo) {
                          applyDateFilter("custom", null, null, null, null, date, customDateTo);
                        }
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <span className="text-sm text-slate-600">To:</span>
                <Popover open={showToCalendar} onOpenChange={setShowToCalendar}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-40 justify-start">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {customDateTo ? format(customDateTo, "dd MMM yyyy") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={customDateTo}
                      onSelect={(date) => {
                        setCustomDateTo(date);
                        setShowToCalendar(false);
                        if (customDateFrom && date) {
                          applyDateFilter("custom", null, null, null, null, customDateFrom, date);
                        }
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Button 
                  variant="default" 
                  size="sm"
                  onClick={() => applyDateFilter("custom", null, null, null, null, customDateFrom, customDateTo)}
                  disabled={!customDateFrom || !customDateTo}
                >
                  Apply
                </Button>
              </>
            )}
          </div>

          {/* Quick Filter Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-slate-600">Quick:</span>
            {[
              { key: "today", label: "Today" },
              { key: "yesterday", label: "Yesterday" },
              { key: "thisWeek", label: "This Week" },
              { key: "lastWeek", label: "Last Week" },
              { key: "thisMonth", label: "This Month" },
              { key: "lastMonth", label: "Last Month" },
              { key: "thisYear", label: "This Year" },
            ].map(({ key, label }) => (
              <Button
                key={key}
                variant="outline"
                size="sm"
                onClick={() => handleQuickFilter(key)}
                className="text-xs h-8"
              >
                {label}
              </Button>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleQuickFilter("clear")}
              className="text-xs h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              Clear Filter
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DateViewSelector;
